
import React from "react";
import type { MortgageData } from "../CalculatorTabs";
import { AutoCalculateOptions } from "../utils/types";
import { EducationalPanel } from "../education/EducationalPanel";

// Import our newly created components
import LocationSelector from "./advanced/LocationSelector";
import PropertyTypeSelector from "./advanced/PropertyTypeSelector";
import CreditScoreSlider from "./advanced/CreditScoreSlider";
import PropertyTaxPIDInput from "./advanced/PropertyTaxPIDInput";
import InsuranceInput from "./advanced/InsuranceInput";
import HOAFeeInput from "./advanced/HOAFeeInput";

interface AdvancedInputsProps {
  mortgageData: MortgageData;
  setMortgageData: React.Dispatch<React.SetStateAction<MortgageData>>;
  autoCalculate: {
    propertyTax: boolean;
    insurance: boolean;
    pmi: boolean;
    closingCosts: boolean;
  };
  setAutoCalculate: React.Dispatch<React.SetStateAction<{
    propertyTax: boolean;
    insurance: boolean;
    pmi: boolean;
    closingCosts: boolean;
  }>>;
  states: {id: string, name: string, abbreviation: string}[];
  cities: {id: string, name: string}[];
}

const AdvancedInputs: React.FC<AdvancedInputsProps> = ({ 
  mortgageData, 
  setMortgageData, 
  autoCalculate, 
  setAutoCalculate, 
  states, 
  cities 
}) => {
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-foreground mb-3">Advanced Details</h3>
      
      {/* Location Selection */}
      <LocationSelector
        mortgageData={mortgageData}
        setMortgageData={setMortgageData}
        autoCalculate={autoCalculate}
        states={states}
        cities={cities}
      />
      
      {/* Property Type */}
      <PropertyTypeSelector
        mortgageData={mortgageData}
        setMortgageData={setMortgageData}
        autoCalculate={autoCalculate}
      />
      
      {/* Credit Score */}
      <CreditScoreSlider
        mortgageData={mortgageData}
        setMortgageData={setMortgageData}
        autoCalculate={autoCalculate}
      />
      
      {/* Property Tax & PID */}
      <PropertyTaxPIDInput
        mortgageData={mortgageData}
        setMortgageData={setMortgageData}
        autoCalculate={autoCalculate}
        setAutoCalculate={setAutoCalculate}
      />
      
      {/* Insurance */}
      <InsuranceInput
        mortgageData={mortgageData}
        setMortgageData={setMortgageData}
        autoCalculate={autoCalculate}
        setAutoCalculate={setAutoCalculate}
      />
      
      {/* HOA Fee */}
      <HOAFeeInput
        mortgageData={mortgageData}
        setMortgageData={setMortgageData}
        autoCalculate={autoCalculate}
      />
      
      {/* Educational Content Panel - Collapsible on mobile */}
      <details className="pt-4 mt-4 border-t border-border">
        <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-3">
          📚 Learn More
        </summary>
        <div className="pt-2">
          <EducationalPanel mortgageData={mortgageData} />
        </div>
      </details>
    </div>
  );
};

export default AdvancedInputs;
